import type { DacVerdict } from "./verdict.js";

/**
 * THE CONSENT PIN
 *
 * CONSENT_TEXT is the exact sentence rendered next to the checkbox on step 2.
 * CONSENT_VERSION is recorded against every lead, so a record in Zoho can be
 * traced back to the words the customer actually saw.
 *
 * CONSENT_TEXT_SHA256 is what makes that traceability real rather than
 * aspirational, but a hash stored beside the value it hashes only proves
 * internal consistency at HEAD: on its own it cannot tell you the version
 * string wasn't left stale. So CONSENT_VERSION is not a free-standing label,
 * it is built from the hash: the format is `<date>.<first 8 hex chars of
 * CONSENT_TEXT_SHA256>`. Editing CONSENT_TEXT and recomputing the hash without
 * updating that suffix now fails a dedicated test (see consentPin.test.ts),
 * which is what actually turns a stale version into a CI failure. The date
 * segment is free to change on its own, so re-dating an unchanged sentence
 * still works.
 *
 * To change the consent language:
 *   1. Edit CONSENT_TEXT.
 *   2. Recompute the hash with
 *
 *        node -e 'const{createHash}=require("crypto");console.log(createHash("sha256").update(process.argv[1],"utf8").digest("hex"))' "<the new text>"
 *
 *   3. Set CONSENT_TEXT_SHA256 to the full digest.
 *   4. Set CONSENT_VERSION to `<today's date>.<first 8 hex chars of that digest>`.
 *   5. Move the outgoing entry in CONSENT_HISTORY to a closed `effectiveTo` and
 *      prepend the new one. See that constant for why this is not optional.
 *
 * All five edits belong in one commit.
 *
 * WHY THIS SENTENCE IS SHAPED THE WAY IT IS. It is drafted to be prior express
 * WRITTEN consent (PEWC) under 47 CFR 64.1200(f)(9), not merely prior express
 * consent, which is a materially different thing: PEWC is what covers marketing
 * calls and texts placed with an autodialer or an artificial, prerecorded or AI
 * voice, and that is what a CRM sequence actually sends. The predecessor
 * sentence ("...may contact me about my solar project by phone, text, or
 * email") was valid prior express consent and validly overrode Do-Not-Call
 * listing, but it authorised no automated technology, did not state that consent
 * is not a condition of purchase, and read transactional rather than
 * promotional. Three elements therefore have to survive any future edit:
 *
 *   1. Authorisation of automated technology and prerecorded messages.
 *   2. "Consent is not a condition of any purchase."
 *   3. A stated opt-out (STOP).
 *
 * consentPin.test.ts asserts all three. Legal record: docs/legal-review-2026-08-05.md,
 * question 2, Option B. Nico's memo also notes the checkbox is required to see the
 * result, which stays defensible only because the result is free, so item 2 above
 * is literally true. Gating a PAID outcome on this box would change that answer.
 */
export const CONSENT_TEXT =
  "By checking this box, I agree that Kasselman Solar may call, text, and email me " +
  "about my solar project and related Kasselman Solar products and services, at the " +
  "number and email I provided, including calls and texts made with automated " +
  "technology or prerecorded messages. Consent is not a condition of any purchase. " +
  "Message and data rates may apply; reply STOP to any text to opt out.";

/**
 * SHA-256 of `CONSENT_TEXT`, full digest.
 *
 * Not decoration: `CONSENT_VERSION` is derived from its first eight characters, so
 * editing the sentence and recomputing this without updating the version fails
 * `functions/src/consentPin.test.ts`. A hash stored beside the value it hashes only
 * proves internal consistency at HEAD; deriving the version from it gives that
 * consistency a memory.
 *
 * @see README.md "Changing the consent sentence" for the four-edit procedure.
 */
export const CONSENT_TEXT_SHA256 =
  "f4e451c5ce2f04daff358e06b14dc0c3e1bc7f5b94aafeeaafef5e697656ee2e";

/**
 * What a lead records as the wording the customer agreed to.
 *
 * Shape is `<effective date>.<first 8 hex of CONSENT_TEXT_SHA256>`.
 *
 * **The server must never validate this against its own constant.** The widget and
 * the functions deploy on different cadences, so between a `main` deploy and a
 * promotion the two legitimately disagree, and an equality check would reject every
 * real lead in that window. The client is the only accurate witness to what was
 * rendered on screen.
 *
 * @see ADR-0017 for the deploy split this follows from.
 */
export const CONSENT_VERSION = "2026-08-05.f4e451c5"; // date . first 8 of CONSENT_TEXT_SHA256

/**
 * One retired consent wording, with the exact text the customer saw.
 *
 * WHY A HASH WAS NOT ENOUGH. `CONSENT_TEXT_SHA256` proves integrity only for the
 * text sitting next to it. The moment the sentence changes, every lead already in
 * Zoho carries a `consentVersion` naming a sentence that exists nowhere in the
 * running system, and reconstructing it means git archaeology against a
 * deployment date. Two independent reasons that is not good enough:
 *
 *   1. The amended FTC Telemarketing Sales Rule (16 CFR 310.5, in force since
 *      October 2024) requires sellers to retain a record of each consumer's
 *      express consent for five years.
 *   2. In TCPA litigation the burden of proving consent is on us, and what has to
 *      be produced is the disclosure AS THE CONSUMER SAW IT. A digest proves
 *      integrity only if the text it digests can still be produced.
 *
 * WHAT THIS IS NOT. This array is the WORDING archive, not the consent archive.
 * The per-lead facts (which version, which timestamp, checkbox state, the number
 * and email given) live in Zoho, because this service keeps no datastore. Both
 * halves are needed to answer "what did this person agree to, and when", and the
 * five-year clock runs on the Zoho side from last contact with the lead. Retiring
 * a wording here does NOT permit deleting the leads captured under it.
 *
 * Ordered newest first. The live wording is always index 0 and always the only
 * entry with a null `effectiveTo`; consentPin.test.ts enforces both, along with
 * every entry's digest, so a hand-edited archive fails CI rather than quietly
 * becoming fiction.
 */
export type ConsentRecord = {
  readonly version: string;
  readonly text: string;
  readonly sha256: string;
  /** ISO date this wording began being shown. */
  readonly effectiveFrom: string;
  /** ISO date it stopped, or null for the wording live right now. */
  readonly effectiveTo: string | null;
};

/**
 * Every consent wording this widget has ever shown, newest first, with the exact
 * text. The current one has `effectiveTo: null`.
 *
 * A hash alone proves integrity for the wording at HEAD and nothing about a lead
 * consented under a wording since replaced. Answering "what did this customer
 * actually agree to on that date" needs the retired text itself, so it is kept here
 * rather than recoverable only from git.
 */
export const CONSENT_HISTORY: readonly ConsentRecord[] = Object.freeze([
  Object.freeze({
    version: CONSENT_VERSION,
    text: CONSENT_TEXT,
    sha256: CONSENT_TEXT_SHA256,
    effectiveFrom: "2026-08-05",
    effectiveTo: null,
  }),
  Object.freeze({
    // Prior express consent, but NOT prior express written consent: it authorised
    // no automated technology. Leads captured under this version are covered for
    // human-dialled calls and human-initiated texts only. That distinction is the
    // reason this entry has to remain legible rather than being a hash.
    version: "2026-07-29.925f8948",
    text: "I agree that Kasselman Solar may contact me about my solar project by phone, text, or email.",
    sha256: "925f8948ac45aa9de5884e3f86243b922001dd9e4e496745989d40c56be3745c",
    effectiveFrom: "2026-07-29",
    effectiveTo: "2026-08-05",
  }),
]);

/**
 * The wording a recorded `consentVersion` refers to, or undefined if the archive
 * has no such version.
 *
 * Undefined rather than a throw or a fallback string: this is what answers a
 * discovery request or a complaint, and inventing plausible text for an unknown
 * version is the one behaviour that would make the archive worse than useless.
 */
export function consentTextForVersion(version: string): string | undefined {
  return CONSENT_HISTORY.find((r) => r.version === version)?.text;
}

/** Customer-supplied contact details. Phone is optional per Marketing's requirement. */
export type Contact = {
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
};

/**
 * The consent record forwarded with every lead.
 *
 * `contactConsent` is typed as the literal `true` because there is no such thing
 * as a lead carrying a false consent: the server rejects the request instead.
 * Typing it `boolean` would make the impossible state representable and invite a
 * downstream reader to branch on it.
 */
export type Consent = {
  contactConsent: true;
  /** ISO 8601, set by the client when the box was ticked. */
  contactConsentAt: string;
  /**
   * The CONSENT_VERSION the client rendered, not the server's own. The widget
   * and the functions deploy on different cadences, so between a `main` deploy
   * and a `production` promotion the two legitimately disagree about which
   * version is current, and only the client knows what was actually rendered
   * on screen when the box was ticked.
   */
  consentVersion: string;
};

/** Where the flow was running. Mirrors generator-sales-tool's SubmissionSource. */
export type LeadSource = {
  site: string;
  page: string;
  embed: "embed" | "standalone";
};

/** Maximum characters accepted for any single free-text field. */
export const MAX_FIELD_LEN = 200;

/** Longest address we will even attempt. Matches the internal endpoint. */
export const MAX_ADDRESS_LEN = 1000;

/**
 * The n8n lead payload. n8n owns the Zoho write (parent spec D12), so this is
 * the entire contract between the widget and the CRM.
 */
export type LeadPayload = {
  schemaVersion: typeof LEAD_SCHEMA_VERSION;
  submissionId: string;
  /** ISO 8601. */
  submittedAt: string;
  source: LeadSource;
  contact: Contact;
  address: {
    /** Exactly what the customer typed. */
    entered: string;
    /** The geocoder's canonical form, carried in the signed token. */
    matched: string;
  };
  /**
   * Normally the resolved arm. It can be `tract_not_in_dataset` if a deploy
   * swapped the vintage between step 1 and step 2, which is rare and handled
   * rather than prevented: a consenting customer is a lead either way.
   */
  verdict: DacVerdict;
  consent: Consent;
};

/**
 * Wire-contract version for `LeadPayload`, read by the n8n intake.
 *
 * Bump only when a field is added, removed or retyped. Behaviour changes that leave
 * the shape alone do not touch it: enforcing the HMAC and adding dedupe both left
 * this at 1, because the n8n contract was unchanged.
 */
export const LEAD_SCHEMA_VERSION = 1 as const;

/**
 * Retry budget for the n8n handoff. Total attempts = length + 1.
 *
 * The sum of these delays plus the per-attempt fetch timeout must stay under the
 * callable timeout, or the customer sees a failure for a lead that actually
 * arrived. 1000 + 2000 + 4000 = 7s of sleeping, plus at most 4 x 5s of fetch,
 * is 27s against a 60s callable timeout.
 */
export const LEAD_RETRY_BACKOFF_MS: readonly number[] = [1000, 2000, 4000];

/** Per-attempt fetch timeout for the n8n POST. */
export const LEAD_ATTEMPT_TIMEOUT_MS = 5000;
